import { NgFor, NgIf } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, Component, ElementRef, Input, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { MatStepperModule } from '@angular/material/stepper';
import { ApiLinks } from '@cutting/app.api-links';
import { MaterialForReplacementToElement, PriceList, ProductModelViewerDialogComponent } from 'cloud-shared-lib';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'cutting-product-images-viewer',
  templateUrl: './product-images-viewer.component.html',
  styleUrls: ['./product-images-viewer.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    NgFor,
    MatStepperModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    ProductModelViewerDialogComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  encapsulation: ViewEncapsulation.None,
})
export class ProductImagesViewerComponent {
  @Input() userId = 0;
  @Input() imagePaths: string[] = [];
  @Input() productId = 0;
  @Input() priceList: PriceList;

  @ViewChild('viewer', { static: false }) viewer: ElementRef;

  exposure = 0.5;

  modelMaterials: any[] = [];

  productModelViewerDialog: MatDialogRef<ProductModelViewerDialogComponent>;

  get modelUrl(): string {
    return `${ApiLinks.elasticModels}/products/${this.priceList.id}/${this.productId}/view-price-list-model`;
  }

  constructor(public dialog: MatDialog) {}

  getPathToTexture(textureName: string): string {
    if (this.userId === 0) {
      console.error('(c) Not enough parameters to get texture');
      return '';
    }
    return `${ApiLinks.elasticModelTexture}/${this.userId}/${textureName}`;
  }

  getProductPreviewPath(productId: number): string {
    return `${ApiLinks.elasticModelPriceList}/${this.priceList.id}/${productId}/preview`;
  }

  showProductModel(): void {
    this.productModelViewerDialog = this.dialog.open(ProductModelViewerDialogComponent, {
      disableClose: false,
      panelClass: 'product-model-viewer-dialog',
      width: '95vw',
      height: '95vh',
      data: {
        modelUrl: this.modelUrl,
      },
    });

    this.productModelViewerDialog
      .afterClosed()
      .pipe(finalize(() => (this.productModelViewerDialog = null)))
      .subscribe();
  }

  changeElementMaterial(elementName: string, material: MaterialForReplacementToElement): void {
    if (!this.viewer) {
      return;
    }

    const materials = this.viewer.nativeElement.model.materials;
    for (const mat of materials) {
      if (mat.name.startsWith(elementName + '=')) {
        if (material.color) {
          mat.pbrMetallicRoughness.setBaseColorFactor(this.hexToRgbA(material.color));
          mat.pbrMetallicRoughness['baseColorTexture'].texture.source.setURI('/assets/images/white_texture.png');
        } else {
          mat.pbrMetallicRoughness.setBaseColorFactor([1, 1, 1, 1]);
          mat.pbrMetallicRoughness['baseColorTexture'].texture.source.setURI(material.textureLink);
        }
      }
    }
  }

  hexToRgbA(hex: string): number[] {
    let c: string[] = [];
    if (/^([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
      c = hex.split('');
      if (c.length === 3) {
        c = [c[0], c[0], c[1], c[1], c[2], c[2]];
      }
      const res: number = +('0x' + c.join(''));
      // eslint-disable-next-line no-bitwise
      return [((res >> 16) & 255) / 255, ((res >> 8) & 255) / 255, (res & 255) / 255, 1];
    }
    throw new Error('Bad Hex');
  }
}
